import { useEffect, useRef } from 'react'
import { useThemeMode } from '@/contexts/ThemeContext'

interface Dot {
  x: number
  y: number
  size: number
  opacity: number
  vx: number
  vy: number
}

export default function StippledBackground() {
  const { isDark, colors } = useThemeMode()
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let dots: Dot[] = []

    // Function to generate dots based on current canvas dimensions
    const generateDots = (width: number, height: number) => {
      const newDots: Dot[] = []
      const numDots = 8000 // High density for stippling effect

      // Create flowing regions with different dot densities
      for (let i = 0; i < numDots; i++) {
        const x = Math.random() * width
        const y = Math.random() * height

        // Create organic density variations using noise-like patterns
        const centerX = width / 2
        const centerY = height / 2
        const distFromCenter = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2)
        const maxDist = Math.sqrt(centerX ** 2 + centerY ** 2)
        const normalizedDist = distFromCenter / maxDist

        // Create flowing patterns with sine waves
        const flowX = Math.sin(x / 200 + y / 300) * 0.5 + 0.5
        const flowY = Math.cos(y / 250 + x / 350) * 0.5 + 0.5
        const flow = (flowX + flowY) / 2

        // Combine distance and flow for organic density
        const density = flow * (1 - normalizedDist * 0.3)

        // Only create dot if it passes density threshold
        if (Math.random() < density) {
          newDots.push({
            x,
            y,
            size: 0.5 + Math.random() * 1.5, // Vary dot sizes
            opacity: 0.3 + Math.random() * 0.5,
            vx: (Math.random() - 0.5) * 0.05, // Very slow drift
            vy: (Math.random() - 0.5) * 0.05,
          })
        }
      }

      return newDots
    }

    const updateSize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
      // Regenerate dots with new dimensions
      dots = generateDots(canvas.width, canvas.height)
    }
    updateSize()

    // Debounce resize handler to avoid expensive regeneration on every event
    let resizeTimeoutId: number | undefined
    const handleResize = () => {
      if (resizeTimeoutId !== undefined) {
        clearTimeout(resizeTimeoutId)
      }
      resizeTimeoutId = window.setTimeout(() => {
        updateSize()
      }, 150)
    }
    window.addEventListener('resize', handleResize)

    let animationId: number
    let time = 0

    const animate = () => {
      time += 0.001

      // First layer: hardcoded black or white (100% opacity)
      ctx.fillStyle = isDark ? '#000000' : '#FFFFFF'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Second layer: primaryBg from theme with 40% opacity overlay
      const primaryBg = colors.primaryBg

      function hexToRgba(hex: string, alpha: number) {
        let c = hex.replace('#', '')
        if (c.length === 3) c = c[0]+c[0]+c[1]+c[1]+c[2]+c[2]
        const num = parseInt(c, 16)
        const r = (num >> 16) & 255
        const g = (num >> 8) & 255
        const b = num & 255
        return `rgba(${r},${g},${b},${alpha})`
      }

      const overlayOpacity = isDark ? 0.5 : 0.7

      let overlayStyle: string
      if (primaryBg.startsWith('#')) {
        overlayStyle = hexToRgba(primaryBg, overlayOpacity)
      } else if (primaryBg.startsWith('rgb')) {
        // Already in rgb/rgba, replace alpha with mode-specific opacity
        const rgbMatch = primaryBg.match(/^rgba?\(([\d\s.,]+)\)$/)
        if (rgbMatch) {
          const parts = rgbMatch[1].split(',').map(part => parseFloat(part))
          const [r, g, b] = parts
          overlayStyle = `rgba(${r},${g},${b},${overlayOpacity})`
        } else {
          overlayStyle = primaryBg
        }
      } else {
        // Fallback
        overlayStyle = isDark ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.7)'
      }

      ctx.fillStyle = overlayStyle
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Draw dots
      dots.forEach((dot) => {
        // Very subtle drift
        dot.x += dot.vx
        dot.y += dot.vy

        // Wrap around edges
        if (dot.x < 0) dot.x = canvas.width
        if (dot.x > canvas.width) dot.x = 0
        if (dot.y < 0) dot.y = canvas.height
        if (dot.y > canvas.height) dot.y = 0

        // Subtle opacity pulsing based on position and time
        const pulse = Math.sin(dot.x / 500 + dot.y / 500 + time * 2) * 0.1
        const finalOpacity = Math.max(0.2, Math.min(0.8, dot.opacity + pulse))

        ctx.fillStyle = isDark
          ? `rgba(255, 255, 255, ${finalOpacity * 0.3})`
          : `rgba(0, 0, 0, ${finalOpacity * 0.15})` // More visible
        ctx.beginPath()
        ctx.arc(dot.x, dot.y, dot.size, 0, Math.PI * 2)
        ctx.fill()
      })

      animationId = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      if (resizeTimeoutId !== undefined) {
        clearTimeout(resizeTimeoutId)
      }
      window.removeEventListener('resize', handleResize)
      cancelAnimationFrame(animationId)
    }
  }, [isDark, colors]) // Re-run effect when theme or colors change

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0"
      style={{ zIndex: 0 }}
    />
  )
}
